import { LoaderCircle } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { Button } from './button';
import { cn } from '../../lib/utils';

type ButtonProps = ComponentProps<typeof Button>;

type Props = Omit<ButtonProps, 'children'> & {
  /** The brand mark — typically a small SVG (e.g. the Google "G"). */
  icon: ReactNode;
  /** Visible label, e.g. "Continue with Google". */
  label: string;
  /** When true, swaps the icon+label for a spinner + `loadingLabel`. */
  loading?: boolean;
  /** Label shown while `loading` is true. */
  loadingLabel?: string;
};

/**
 * Brand sign-in button shared across the webapp (Google OAuth) and the
 * Overwolf desktop app (Overwolf profile login). Encapsulates the shared
 * visual language: primary-filled pill, brand glyph + label, a left-to-right
 * gradient sheen on hover, and a spinner-with-label loading state.
 *
 * Use this as a building block — pair it with whatever auth trigger the
 * caller needs (a plain `onClick`, or an invisible `<GoogleLogin>` overlay
 * sitting on top to catch the click, etc.).
 */
export function OAuthButton({
  icon,
  label,
  loading,
  loadingLabel = 'Signing you in…',
  className,
  disabled,
  ...rest
}: Props) {
  return (
    <Button
      type="button"
      size="lg"
      className={cn(
        'h-10 w-full relative overflow-hidden group',
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
      {loading ? (
        <>
          <LoaderCircle className="h-5 w-5 animate-spin" />
          {loadingLabel}
        </>
      ) : (
        <>
          {icon}
          {label}
        </>
      )}
    </Button>
  );
}
